import React, { useState, useRef } from 'react';
import '../LoginPage/LoginPage.css';
import '../ChangePassword/ChangePassword.css';
import { apiCall } from '../../APICalls/APICalls';

/**
 * The screen a password-reset link lands on.
 *
 * Reached by URL rather than by navigation - App.js renders this instead of
 * the login form whenever ?resetToken= is present - because the person
 * arriving here is by definition unable to sign in.
 *
 * Nothing about the token is inspected locally: whether it is real, unexpired
 * and unspent is only ever answered by the server, and it is answered once,
 * at redemption. A link that turns out to be stale therefore fails after the
 * user has typed a password, which is why the failure message says plainly
 * that a new link is needed.
 */
function ResetPassword(props) {

    const newRef = useRef(null);
    const confirmRef = useRef(null);

    const [ errors, setErrors ] = useState([]);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ isDone, setIsDone ] = useState(false);

    function describeError(error) {
        if (!error.response) {
            return ['Could not reach the server. Check your connection and try again.'];
        }
        const status = error.response.status;
        const data = error.response.data || {};

        if (status === 400 && data.error === 'weak_password') {
            // The token is spent by the time the server checks the password,
            // so this is not a retryable field error - say so.
            return (data.messages || ['That password does not meet the requirements.'])
                .concat(['This link has now been used. Request a new one to try again.']);
        }
        if (status === 400 && data.error === 'invalid_token') {
            return ['This reset link is no longer valid. It may have expired, '
                + 'already been used, or been replaced by a newer one. Request a new link.'];
        }
        if (status === 403 && data.error === 'account_inactive') {
            return ['This account is not active. Contact an administrator.'];
        }
        if (status === 429) {
            return ['Too many attempts. Please wait a few minutes and try again.'];
        }
        return ['Could not reset the password. Please try again.'];
    }

    function handleSubmit() {
        if ( isSubmitting ) return;

        const newPassword = newRef.current ? newRef.current.value : '';
        const confirmPassword = confirmRef.current ? confirmRef.current.value : '';

        if ( !newPassword ) {
            setErrors(['Enter a new password.']);
            return;
        }
        if ( newPassword !== confirmPassword ) {
            setErrors(['The passwords do not match.']);
            return;
        }

        setErrors([]);
        setIsSubmitting(true);

        apiCall('auth/completeReset', {
            token: props.token,
            newPassword: newPassword
        }).then( res => {
            if ( res.data && res.data.success === true ) {
                setIsDone(true);
            } else {
                setErrors(['Could not reset the password. Please try again.']);
            }
            setIsSubmitting(false);
        }).catch( error => {
            setErrors( describeError( error ) );
            setIsSubmitting(false);
        });
    }

    function handleKeyDown(e) {
        if ( e.keyCode === 13 ) handleSubmit();
    }

    // Strips the token out of the address bar on the way to the login form, so
    // it is not left sitting in history or copied out of the URL later.
    function goToLogin() {
        if ( props.onFinished ) props.onFinished();
    }

    if ( isDone ) {
        return (
            <div id="login_page">
                <div id="login_container">
                    <h1 className="login_title">Password Reset</h1>
                    <p className="change_password_intro">
                        Your password has been changed, and any sessions that were
                        open have been signed out. Sign in with your new password.
                    </p>
                    <div className="login_button_container">
                        <button id="SSO_button" className="BaseButton" onClick={ goToLogin }>
                            Go to Sign In
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="login_page">
            <div id="login_container">
                <h1 className="login_title">Choose a New Password</h1>

                {
                    errors.length > 0 &&
                    <ul className="change_password_errors">
                        { errors.map( (message, i) => (
                            <li key={ i } className="login_error">{ message }</li>
                        )) }
                    </ul>
                }

                <div className="loginInputContainer">
                    <label className="loginInputLabel" htmlFor="newPassword">
                        New password
                    </label>
                    <input
                        type="password"
                        id="newPassword"
                        className="loginInput"
                        autoComplete="new-password"
                        ref={ newRef }
                        onKeyDown={ handleKeyDown }
                    />
                    <p className="change_password_hint">
                        At least 12 characters. A memorable phrase of a few words is
                        both stronger and easier to type than a short password with
                        substituted symbols. It cannot contain your name or email
                        address.
                    </p>
                </div>

                <div className="loginInputContainer">
                    <label className="loginInputLabel" htmlFor="confirmPassword">
                        Confirm new password
                    </label>
                    <input
                        type="password"
                        id="confirmPassword"
                        className="loginInput"
                        autoComplete="new-password"
                        ref={ confirmRef }
                        onKeyDown={ handleKeyDown }
                    />
                </div>

                <div className="login_button_container">
                    <button
                        id="SSO_button"
                        className="BaseButton"
                        onClick={ handleSubmit }
                        disabled={ isSubmitting }
                    >
                        { isSubmitting ? 'Saving...' : 'Set Password' }
                    </button>
                    <button className="change_password_signout" onClick={ goToLogin }>
                        Back to sign in
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;
